/**
 * Recruitment Tab Component
 * Recruitment pipeline for Volunteer Management
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Mail, Calendar, ClipboardCheck, AlertCircle, Plus } from "lucide-react";

// Role types for displaying interest areas
const ROLE_TYPES = {
  companionship: { label: "Companionship" },
  bereavement: { label: "Bereavement Support" },
  threshold_singers: { label: "Threshold Singers" },
  pet_therapy: { label: "Pet Therapy" },
  administrative: { label: "Administrative" },
  transportation: { label: "Transportation" },
  respite: { label: "Respite Care" }
};

const RecruitmentTab = ({
  isDark,
  inquiries,
  interviews,
  volunteers,
  volunteerRequests,
  orientationModules,
  setInquiryDialog
}) => {
  return (
    <div className="space-y-4">
      {/* Recruitment Pipeline Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>New Inquiries</p>
                <p className="text-2xl font-bold text-blue-500">{inquiries.filter(i => i.status === "new").length}</p>
              </div>
              <Mail className="w-8 h-8 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Scheduled Interviews</p>
                <p className="text-2xl font-bold text-purple-500">{interviews.filter(i => i.status === "scheduled").length}</p>
              </div>
              <Calendar className="w-8 h-8 text-purple-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>In Onboarding</p>
                <p className="text-2xl font-bold text-yellow-500">
                  {volunteers.filter(v => v.status === "onboarding").length}
                </p>
              </div>
              <ClipboardCheck className="w-8 h-8 text-yellow-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>Pending Requests</p>
                <p className="text-2xl font-bold text-orange-500">{volunteerRequests.length}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-orange-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inquiries Section */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Volunteer Inquiries</CardTitle>
            <CardDescription>Prospective volunteers who have expressed interest</CardDescription>
          </div>
          <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => setInquiryDialog(true)} data-testid="add-inquiry-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Inquiry
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? "border-slate-800" : ""}>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Interest Areas</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inquiries.map((inquiry) => (
                <TableRow key={inquiry.id} className={isDark ? "border-slate-800" : ""} data-testid={`inquiry-row-${inquiry.id}`}>
                  <TableCell className="font-medium">{inquiry.name}</TableCell>
                  <TableCell>{inquiry.email || inquiry.phone}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(inquiry.interest_areas || []).map((area, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {ROLE_TYPES[area]?.label || area}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      inquiry.status === "new" ? "bg-blue-500/20 text-blue-400" :
                      inquiry.status === "contacted" ? "bg-yellow-500/20 text-yellow-400" :
                      "bg-green-500/20 text-green-400"
                    }>
                      {inquiry.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{inquiry.created_at?.split("T")[0]}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm">
                      Convert to Volunteer
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {inquiries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <p className={isDark ? "text-gray-500" : "text-gray-400"}>No inquiries yet</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Upcoming Interviews */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-lg">Upcoming Interviews</CardTitle>
          <CardDescription>Scheduled interviews with prospective volunteers</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {interviews.filter(i => i.status === "scheduled").map((interview) => (
              <div 
                key={interview.id}
                className={`p-4 rounded-lg flex items-center justify-between ${isDark ? "bg-slate-800" : "bg-gray-50"}`}
                data-testid={`interview-item-${interview.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isDark ? "bg-purple-500/20" : "bg-purple-100"}`}>
                    <Calendar className="w-6 h-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="font-medium">{interview.volunteer_id}</p>
                    <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      {interview.interview_date} at {interview.interview_time}
                    </p>
                    <p className={`text-sm ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                      Interviewer: {interview.interviewer_name}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">Reschedule</Button>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700">Complete</Button>
                </div>
              </div>
            ))}
            {interviews.filter(i => i.status === "scheduled").length === 0 && (
              <p className={`text-center py-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                No interviews scheduled
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Orientation Modules */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-lg">Orientation Curriculum</CardTitle>
          <CardDescription>Required training modules for new volunteers</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {orientationModules.map((module) => (
              <div 
                key={module.module_id}
                className={`p-4 rounded-lg border ${isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200"}`}
                data-testid={`orientation-module-${module.module_id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <Badge className={
                    module.content_type === "video" ? "bg-blue-500/20 text-blue-400" :
                    module.content_type === "document" ? "bg-green-500/20 text-green-400" :
                    "bg-purple-500/20 text-purple-400"
                  }>
                    {module.content_type}
                  </Badge>
                  <span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    {module.duration_minutes} min
                  </span>
                </div>
                <h4 className="font-medium text-sm mb-1">{module.title}</h4>
                <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  {module.description}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RecruitmentTab;
